// d:\Code\debate-app\src\components\PrepTimer.js
import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import { ref, onValue, update } from 'firebase/database';
import { db } from '../firebase';
import {
    Typography,
    Button,
    Box,
    LinearProgress
} from '@mui/material';

export default function PrepTimer({
    sessionId,
    side,
    label,
    roleMatch,
    totalPrepTime,
    isSegmentTimerRunning,
    serverTimeOffset = 0,
    isOtherPrepTimerRunning,
}) {
  // Local state mirroring Firebase data - drives the UI
  const [timer, setTimer] = useState({ remaining: totalPrepTime ?? 0, running: false });
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  // --- ADDED: Ref to hold the latest timer state for the interval ---
  const timerRefInternal = useRef(timer);

  // Effect to fetch and listen for prep timer data from Firebase
  useEffect(() => {
    const timerDbRef = ref(db, `sessions/${sessionId}/prepTimers/${side}`);
    const unsubscribe = onValue(timerDbRef, snap => {
      const val = snap.val();
      let updatedTimerState;
      if (val) {
        updatedTimerState = {
            ...val,
            remaining: Math.min(val.remaining, totalPrepTime ?? 0)
        };
      } else {
        console.warn(`Prep timer data for side ${side} not found. Initializing.`);
        updatedTimerState = { remaining: totalPrepTime ?? 0, running: false };
      }
      // Update both the React state (for UI) and the ref (for the interval)
      setTimer(updatedTimerState);
      timerRefInternal.current = updatedTimerState; // Keep ref in sync
      setInitialFetchDone(true);
    }, (error) => {
        console.error(`Firebase read failed for prep timer ${side}:`, error);
        setInitialFetchDone(true);
    });
    return () => unsubscribe();
  }, [sessionId, side, totalPrepTime]); // Dependencies are correct

  // Effect to *trigger* Firebase updates via setInterval when running
  useEffect(() => {
    let interval = null;

    // Condition to run the timer interval
    if (timer.running && roleMatch && !isSegmentTimerRunning) {
      interval = setInterval(() => {
        // --- MODIFICATION START ---
        // Read the latest state directly from the ref
        const currentTimerState = timerRefInternal.current;

        // Check if the timer should still be running based on the ref's state
        if (!currentTimerState.running || currentTimerState.remaining <= 0) {
           // If state changed externally, clear interval
           if (interval) clearInterval(interval);
           return; // Stop the callback execution
        }

        // Calculate the next value based on the ref's state
        const currentRemaining = currentTimerState.remaining;
        const newRem = Math.max(0, currentRemaining - 1000);

        // Prepare the Firebase update object
        const updates = { remaining: newRem };
        if (newRem === 0) {
          updates.running = false; // Stop running when time hits zero
          if (interval) clearInterval(interval); // Clear interval immediately
        }

        // Update Firebase using the calculated new value
        // The onValue listener will eventually update the 'timer' state and the ref
        update(ref(db, `sessions/${sessionId}/prepTimers/${side}`), updates)
          .catch(error => console.error(`Failed to update prep timer ${side}:`, error));

        // --- MODIFICATION END ---
      }, 1000); // Run every 1 second

    } else if (timer.running && roleMatch && isSegmentTimerRunning) {
      // Auto-pause if segment timer starts while prep is running
      console.log(`Segment timer running, pausing prep timer for ${side}`);
      update(ref(db, `sessions/${sessionId}/prepTimers/${side}`), { running: false })
        .catch(error => console.error(`Failed to auto-pause prep timer ${side}:`, error));
    }

    // Cleanup: Clear interval if the effect re-runs or component unmounts
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
    // Dependencies remain the same - only react to changes in running conditions
  }, [timer.running, roleMatch, isSegmentTimerRunning, sessionId, side]); // Re-run only when running status or conditions change

  // Function to toggle the running state of the timer in Firebase
  const toggle = () => {
    // Read the latest state directly from the UI state variable before toggling
    const currentRemaining = timer.remaining;
    const currentRunning = timer.running;

    // Prevent action if not the user's role or no time left (allow pausing if 0)
    if (!roleMatch || (currentRemaining <= 0 && !currentRunning)) return;

    const newRunningState = !currentRunning;

    // Prevent STARTING if segment timer OR the other prep timer is running
    if (newRunningState && (isSegmentTimerRunning || isOtherPrepTimerRunning)) {
        const reason = isSegmentTimerRunning ? "segment timer" : "other prep timer";
        console.log(`Cannot start prep timer while ${reason} is running.`);
        alert(`Cannot start prep timer while ${reason} is running.`);
        return;
    }

    // Update Firebase - the onValue listener will update the local state and the ref
    update(ref(db, `sessions/${sessionId}/prepTimers/${side}`), {
      running: newRunningState
    }).catch(error => console.error(`Failed to toggle prep timer ${side}:`, error));
  };

  // Calculate display values based on local state (which mirrors Firebase)
  const minutes = Math.floor(timer.remaining / 60000);
  const seconds = Math.floor((timer.remaining % 60000) / 1000);
  const progress = totalPrepTime > 0 ? (timer.remaining / totalPrepTime) * 100 : 0;

  // Determine if the button should be disabled
  const isDisabled = !roleMatch ||
                     (timer.remaining <= 0 && !timer.running) || // Disable start if 0 time
                     (!timer.running && (isSegmentTimerRunning || isOtherPrepTimerRunning)); // Disable start if other timers running

  // Determine button title for disabled reason
  let buttonTitle = "";
  if (!roleMatch) buttonTitle = "Cannot control opponent's timer";
  else if (timer.remaining <= 0 && !timer.running) buttonTitle = "No time remaining";
  else if (!timer.running && isSegmentTimerRunning) buttonTitle = "Segment timer is running";
  else if (!timer.running && isOtherPrepTimerRunning) buttonTitle = "Other prep timer is running";

  // Display loading indicator until initial data is fetched
  if (!initialFetchDone) {
      return <Box sx={{ my: 2 }}><Typography variant="body2">Loading prep...</Typography></Box>;
  }

  // Render the component
  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="subtitle1">{`${label} Prep`}</Typography>
      {totalPrepTime > 0 && <LinearProgress variant="determinate" value={progress} sx={{ my: 1 }} />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
            variant="h6"
            sx={{
                fontVariantNumeric: 'tabular-nums',
                color: timer.remaining <= 0 ? 'error.main' : 'inherit' // Turn red if 0 or less
            }}
        >
            {`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`}
        </Typography>
        <Button
          variant="contained" size="small"
          color={timer.running ? 'warning' : 'primary'}
          disabled={isDisabled} title={isDisabled ? buttonTitle : ""}
          onClick={toggle}
        >
          {timer.running ? 'Pause' : 'Start'}
        </Button>
      </Box>
    </Box>
  );
}
